import React, {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { CSSTransition } from "react-transition-group";
import { type Message, SnackBar } from "~/components/SnackBar/SnackBar";
import "./style.css";

export type SnackBarOpener = (message: Message) => void;

export type SnackBarCloser = () => void;

export const SnackBarContext = React.createContext<{
	open: SnackBarOpener;
	close: SnackBarCloser;
} | null>(null);

interface Props {
	children: ReactNode;
}

export const SnackBarProvider: React.FC<Props> = ({ children }) => {
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [message, setMessage] = useState<Message>({ type: "info", text: "" });
	const nodeRef = useRef(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Workaround to prevent endless loop
	const isOpenedRef = useRef<boolean>(false);
	useEffect(() => {
		isOpenedRef.current = isOpened;
	}, [isOpened]);

	const open = useCallback((message: Message) => {
		// NOTE: this implementation is not perfect, but it's good enough for now
		// because we don't have a queue for snackbar messages
		if (isOpenedRef.current) {
			return;
		}
		setMessage(message);
		setIsOpened(true);
	}, []);

	const close = useCallback(() => {
		setIsOpened(false);
	}, []);

	const handleEntered = useCallback(() => {
		timeoutRef.current = setTimeout(() => {
			setIsOpened(false);
		}, 5000);
	}, []);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return (
		<SnackBarContext.Provider value={{ open, close }}>
			<CSSTransition
				in={isOpened}
				nodeRef={nodeRef}
				classNames="t4-snackbar-animation"
				timeout={200}
				unmountOnExit
				onEntered={handleEntered}
			>
				<div className="t4-snackbar-animation" ref={nodeRef}>
					<SnackBar message={message} close={close} />
				</div>
			</CSSTransition>
			{children}
		</SnackBarContext.Provider>
	);
};
